import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { IAccount } from "../../models/account/IAccount";
import { CheckboxInput } from "../shared/Inputs/CheckboxInput";
import SelectInput from "../shared/Inputs/SelectInput";
import { TextInput } from "../shared/Inputs/TextInput";
import { AccountPrimaryContactSection } from "./AccountPrimaryContactSection";
import { IContact } from "../../models/Contact";

interface AccountEditDetailsSectionProps {
  account?: IAccount;
  /** Saving through the staff-level haulier route, so the flag can't be changed here. */
  haulierLocked?: boolean;
  register: any;
  setValue: any;
  watch: any;
  accountTypeOptions: { id: number; name: string }[];
  physicalRoutingOptions: { id: number; name: string }[];
  canEditAccountType: boolean;
  primaryContactOptions: { id: string; name: string }[];
  primaryContact?: IContact;
  setPrimaryContact: (contact: IContact) => void;
  isPrimaryContactOpen: boolean;
  setIsPrimaryContactOpen: (open: boolean) => void;
  onAddContact: () => void;
  onEditContact: () => void;
}

export function AccountEditDetailsSection({
  account,
  haulierLocked = false,
  register,
  setValue,
  watch,
  accountTypeOptions,
  physicalRoutingOptions,
  canEditAccountType,
  primaryContactOptions,
  primaryContact,
  setPrimaryContact,
  isPrimaryContactOpen,
  setIsPrimaryContactOpen,
  onAddContact,
  onEditContact,
}: AccountEditDetailsSectionProps) {
  const hasDefra = CheckFeatureFlag("DEFRA");
  const isHaulier = !!watch("isHaulier");

  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-white p-4">
      <div className="pb-1 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-800">Account</p>
        <p className="text-xs text-gray-500">Core details and ownership</p>
      </div>
      <TextInput
        label="Account Number"
        register={register}
        registerName="accountNumber"
        disabled={true}
        rounded
      />
      <TextInput
        label="Account Name"
        register={register}
        registerName="name"
        rounded
      />
      {canEditAccountType ? (
        <SelectInput
          label="Type"
          register={register}
          registerName="accountType"
          options={accountTypeOptions}
          setValue={setValue}
          watch={watch}
          rounded
        />
      ) : (
        <CheckboxInput
          label="Regulars Account"
          register={register}
          registerName="regularsAccount"
          rounded
        />
      )}
      {canEditAccountType && (
        <SelectInput
          label="Physical Routing Default"
          register={register}
          registerName="physicalRoutingDefault"
          options={physicalRoutingOptions}
          setValue={setValue}
          watch={watch}
          rounded
        />
      )}
      {hasDefra && (
        <div className="space-y-3 rounded-md border border-gray-100 bg-gray-50 p-3">
          <CheckboxInput
            label="Waste carrier (haulier)"
            register={register}
            registerName="isHaulier"
            disabled={haulierLocked}
            rounded
          />
          <p className="text-xs text-gray-500">
            {haulierLocked
              ? "This is a haulier account. Only an administrator can change that."
              : "Hauliers can be picked on the Waste Tracking page. Their movements show under this account."}
          </p>
          {isHaulier && (
            <>
              <TextInput
                label="Carrier registration number"
                register={register}
                registerName="carrierRegistrationNumber"
                placeholder="e.g. CBDU123456"
                required
                rounded
              />
              <TextInput
                label="Usual vehicle registration"
                register={register}
                registerName="haulierVehicleRegistration"
                placeholder="Truck / trailer reg, pre-fills each row"
                rounded
              />
            </>
          )}
        </div>
      )}
      <AccountPrimaryContactSection
        account={account}
        register={register}
        setValue={setValue}
        watch={watch}
        primaryContactOptions={primaryContactOptions}
        primaryContact={primaryContact}
        setPrimaryContact={setPrimaryContact}
        isOpen={isPrimaryContactOpen}
        setIsOpen={setIsPrimaryContactOpen}
        onAddContact={onAddContact}
        onEditContact={onEditContact}
      />
    </div>
  );
}

